/* eslint-disable no-use-before-define */
/* eslint-disable no-promise-executor-return */
import { createSlice } from '@reduxjs/toolkit';
import {
  getDuplicateContacts,
  getAgentsChat,
  setMessengerUserDataAsync,
  messengerManager,
  getContact,
  getLeadsStatus,
  changeLeadStatus,
  getPropertiesToShare,
  getStarredsForEntity,
  getQuickResponses,
  getDevelopmentsToShare,
  deleteWebcontact,
  postCreateChannel,
  deleteMessage,
  sendWhatsappMessage,
  updateChannelData,
  searchChat,
  blockChannel,
  unblockChannel,
  acceptChannel,
  fetchBlockedChannels,
} from './services';
import { getMyContactsFilter, getGroupsFilter, applyTabFilter } from './utils';

// Initial state
const initialState = {
  rightbar: false,
  isModalOpen: false,
  autoAsign: false,
  isLoadingWhatsAppUnlinked: true,
  whatsappLinkedData: null, // Respuesta completa de getWhatsappIsLinked: { user, branch, company, is_linked, data, other_branches_linked }
  whatsappLinkedUser: null, // Instancia vinculada para flujo legacy (FLAG OFF)
  openModalDeleteChannel: false,
  openModalLinkNewBranch: false,
  profileSummaryTokki: null,
  isFetchingProfileSummaryTokki: false,
  agentView: {
    isViewingOtherAgentsChats: false,
    selectedAgentViewChat: null,
    selectedBranchView: null,
  },
  messageReply: undefined,
  playingAudio: null,
  microphonePermissionDenied: false,
  CID: false,
  data: null,
  show: false,
  channelData: false,
  channelUpdated: false,
  user: null,
  quickResponses: { list: null, loading: false },
  whatsapp: {
    loading: false,
    qr: false,
    conected: false,
    showQRModal: false,
    showWhatsappAd: false,
    showWhatsappPausedBanner: true, // Banner que se muestra cuando el flag está apagado y el número está desvinculado
    status: '',
    requestedQRType: null,
    canWriteFromBranch: false,
    branchToLink: null,
    availableSlot: null, // Slot disponible para mostrar LinkThisBranch
    slotsAvailability: null, // { company: { available, slot_id }, branch: { available, slot_id }, user: { available, slot_id } }
    linkingView: {
      isVisible: false,
      currentStep: 'info', // 'info' | 'qr' | 'success' | 'error'
      selectedSlot: null,
      isConfirmed: false,
      isScanned: false,
      duplicatePhoneError: null, // { phoneNumber: string } cuando hay error de número duplicado
    },
  },
  unreads: { channels_amount: 0, whatsapp: 0, Colleague: 0, team: 0 },
  agents: {
    loading: false,
    asingLoader: false,
    list: [],
    agentSelected: [],
  },
  duplicateContacts: {
    loading: false,
    contacts: [],
    total: 0,
  },
  filters: {
    id: '',
    config: {},
    trayID: '',
    trayConfig: {},
    sort: { last_message_at: -1 },
    activeTab: 'my_chats', // 'my_chats' | 'branch' | 'company'
    tabHasUnreads: { my_chats: false, branch: false, company: false },
  },
  permissions: {
    canSeeWebContacts: false,
    hasWhatsappByBranchFlag: false,
  },
  contacts: [],
  leadsStatusList: [],
  leadsStatusRequest: 'idle',
  shareProperties: {
    properties: [],
    pageInfo: false,
    loading: false,
    // Property[] — see toggle contract in sharePropertiesChangeValues
    selectedProperties: [],
    show: false,
    pickerTarget: 'message',
  },
  templateReplySelectedProperty: null,
  featuredProperties: {
    properties: [],
    loading: false,
  },
  members: {
    loading: false,
    list: {},
  },
  attachments: {
    loading: false,
    media: [],
    file: [],
    link: [],
  },
  countAttachments: {
    cid: '',
    amount: 0,
  },
  modal: {
    type: null,
    props: {},
    messageId: null,
  },
  reSendMessageWhatsapp: {
    loading: false,
  },
  // TODO: se deja para tener informacion de los archivos que exceden el tamaño
  fileValidation: {
    hasOversizedFiles: false,
    oversizedFileNames: [],
    validFilesCount: 0,
    totalFilesCount: 0,
  },
  search: {
    loading: false,
    query: '',
    channels: [],
    contacts: [],
    messages: [],
    pagination: {
      page: 1,
      limit: 20,
      total: 0,
      total_pages: 0,
      has_next: false,
      has_previous: false,
    },
  },
  pendingJumpToMessage: {
    messageId: null,
    channelId: null,
    searchQuery: null,
  },
  pendingChatAction: {
    property: null,
  },
  channelListReloadKey: 0, // Key para forzar reload del ChannelList
  channelListError: null, // Error de la última query de canales
  blockedCellphone: null,
  blockLoading: false,
  unblockLoading: false,
  acceptLoading: false,
  blockedNumbersView: {
    isVisible: false,
    channels: [],
    loading: false,
    pagination: {
      page: 1,
      limit: 5,
      total: 0,
      total_pages: 0,
      has_next: false,
      has_previous: false,
    },
  },
  channelCatalog: {},
  channelCatalogByChatId: {},
  activeStreamCid: null,
  channelCatalogFetchInFlight: false,
};

// Actual Slice
export const MessengerSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    setChannelData(state, action) {
      state.channelData = action.payload;
    },

    setUnreads(state, action) {
      // Calcular el valor de channels_amount basado en el modo watcher
      const channelsAmount = state.agentView.isViewingOtherAgentsChats
        ? action.payload.whatsapp + action.payload.Colleague
        : action.payload.channels_amount;

      state.unreads = {
        ...action.payload,
        channels_amount: channelsAmount,
      };
    },

    setAttachments(state, action) {
      const { type, attachments } = action.payload;

      state.attachments.loading = action.payload.loading;
      state.attachments[type] = attachments.attachments;
    },

    setAutoAsign(state, action) {
      state.autoAsign = action.payload;
    },

    setCountAttachments(state, action) {
      state.countAttachments = action.payload;
    },

    setFilter(state, action) {
      const watcherAgentId = state.agentView?.selectedAgentViewChat?.id;
      const selectedBranchId = state.agentView?.selectedBranchView?.id;

      const MEMBER = {
        $in: [watcherAgentId ? watcherAgentId.toString() : state.data.info.id],
      };

      // Helper para construir filtro "all" con WhatsAppGuest filtrado por subtype si no hay permisos
      const getAllConfig = () => {
        // Modo watcher: solo Colleague, WhatsAppContact y WhatsAppGuest con subtype 'contact'
        if (watcherAgentId) {
          const conditions = [
            { type: 'Colleague' },
            { type: 'WhatsAppContact' },
            { type: 'WhatsAppGuest', subtype: 'contact' },
          ];

          return {
            $or: conditions,
            members: MEMBER,
            frozen: false,
          };
        }

        // Modo normal (no watcher)
        // Si el usuario NO tiene permisos, agregar solo WhatsAppGuest con subtype 'contact'
        if (!state.permissions.canSeeWebContacts) {
          const baseTypes = ['team', 'Colleague', 'WhatsAppContact', 'network'];
          const conditions = baseTypes.map((type) => ({ type }));
          conditions.push({ type: 'WhatsAppGuest', subtype: 'contact' });

          return {
            $or: conditions,
            members: MEMBER,
            frozen: false,
          };
        }

        // Si tiene permisos, construir $or explícito según el tray activo
        // para evitar usar `type: { $in: [...] }` a nivel raíz, que puede
        // causar errores 500 en GetStream con channel types custom
        const { trayID } = state.filters;

        if (trayID === 'whatsapp') {
          return {
            $or: [
              { type: 'WhatsAppContact' },
              { type: 'WhatsAppGuest', subtype: 'contact' },
              { type: 'WhatsAppGuest', subtype: 'inquiry' },
            ],
            members: MEMBER,
            frozen: false,
          };
        }

        if (trayID === 'colleague') {
          return {
            type: { $in: ['Colleague'] },
            members: MEMBER,
            frozen: false,
          };
        }

        // tray 'all': todos los tipos sin restricción de tipo
        return {
          $or: [
            { type: 'WhatsAppContact' },
            { type: 'WhatsAppGuest', subtype: 'contact' },
            { type: 'WhatsAppGuest', subtype: 'inquiry' },
            { type: 'team' },
            { type: 'Colleague' },
            { type: 'network' },
          ],
          members: MEMBER,
          frozen: false,
        };
      };

      // Helper para construir filtro "unreads" con la misma lógica
      const getUnreadsConfig = () => {
        // Si el usuario NO tiene permisos, agregar solo WhatsAppGuest con subtype 'contact'
        if (!state.permissions.canSeeWebContacts) {
          const baseTypes = ['team', 'Colleague', 'WhatsAppContact', 'network'];
          const conditions = baseTypes.map((type) => ({ type }));
          conditions.push({ type: 'WhatsAppGuest', subtype: 'contact' });

          return {
            $or: conditions,
            members: MEMBER,
            has_unread: true,
            frozen: false,
          };
        }

        // Si tiene permisos, construir $or explícito según el tray activo
        const { trayID } = state.filters;

        if (trayID === 'whatsapp') {
          return {
            $or: [
              { type: 'WhatsAppContact' },
              { type: 'WhatsAppGuest', subtype: 'contact' },
              { type: 'WhatsAppGuest', subtype: 'inquiry' },
            ],
            members: MEMBER,
            has_unread: true,
            frozen: false,
          };
        }

        if (trayID === 'colleague') {
          return {
            type: { $in: ['Colleague'] },
            members: MEMBER,
            has_unread: true,
            frozen: false,
          };
        }

        return {
          $or: [
            { type: 'WhatsAppContact' },
            { type: 'WhatsAppGuest', subtype: 'contact' },
            { type: 'WhatsAppGuest', subtype: 'inquiry' },
            { type: 'team' },
            { type: 'Colleague' },
            { type: 'network' },
          ],
          members: MEMBER,
          has_unread: true,
          frozen: false,
        };
      };

      const getWatcherConfig = () => {
        const conditions = [
          { type: 'Colleague' },
          { type: 'WhatsAppContact' },
          { type: 'WhatsAppGuest', subtype: 'contact' },
        ];

        return {
          $or: conditions,
          members: MEMBER,
          frozen: false,
        };
      };

      const CONFIGS = {
        all: getAllConfig(),

        my_contacts: getMyContactsFilter(MEMBER, state.filters.trayID),

        unreads: getUnreadsConfig(),

        groups: getGroupsFilter(MEMBER, state.filters.trayID),

        watcher: getWatcherConfig(),
      };

      state.filters.id = action.payload;

      state.filters.sort =
        action.payload === 'unreads'
          ? [{ has_unread: -1 }, { last_message_at: -1 }]
          : { last_message_at: -1 };

      // Aplicar el filtro base y luego el filtro de pestaña
      const baseConfig = CONFIGS[action.payload];

      const filtersWithOwnTypeLogic = ['my_contacts', 'groups'];
      const shouldApplyTabFilter = !filtersWithOwnTypeLogic.includes(
        action.payload
      );

      // Solo aplicar filtro de pestañas si:
      // 1. El filtro seleccionado no tiene su propia lógica de tipo
      // 2. El flag whatsapp_by_branch está activo (feature habilitado)
      const { hasWhatsappByBranchFlag } = state.permissions;

      state.filters.config =
        shouldApplyTabFilter && hasWhatsappByBranchFlag && !watcherAgentId
          ? applyTabFilter(
              baseConfig,
              state.filters.activeTab,
              selectedBranchId,
              state.filters.trayID
            )
          : baseConfig;

      // Limpiar el canal seleccionado al cambiar de filtro
      // Esto evita que se muestre un canal seleccionado que no pertenece al nuevo filtro
      state.channelData = false;
    },

    setActiveTab(state, action) {
      state.filters.activeTab = action.payload;
      // Limpiar el punto rojo cuando cambias a esa pestaña
      state.filters.tabHasUnreads[action.payload] = false;
      // Re-aplicar el filtro actual con la nueva pestaña
      MessengerSlice.caseReducers.setFilter(state, {
        type: 'chat/setFilter',
        payload: state.filters.id || 'all',
      });
    },

    setTabHasUnread(state, action) {
      const { tab, hasUnread } = action.payload;
      if (state.filters.tabHasUnreads[tab] !== undefined) {
        state.filters.tabHasUnreads[tab] = hasUnread;
      }
    },

    setChannelTray(state, action) {
      // Optimización: Sin filtro de tipo para mejorar performance con muchos canales
      const ALLTYPES = {};

      const WAPP = {
        $in: ['WhatsAppContact'],
      };

      if (state.permissions.canSeeWebContacts) {
        WAPP.$in.push('WhatsAppGuest');
      }

      const CONFIGS = {
        all: ALLTYPES,

        whatsapp: WAPP,

        colleague: {
          $in: ['Colleague'],
        },
      };

      state.filters.trayID = action.payload;
      state.filters.trayConfig = CONFIGS[action.payload];

      MessengerSlice.caseReducers.setFilter(state, {
        type: 'chat/setFilter',
        payload: state.filters.id || 'all',
      });
    },

    resetDuplicated(state) {
      Object.assign(state.duplicateContacts, initialState.duplicateContacts);
    },

    showRightbar(state, action) {
      state.rightbar = action.payload;
      if (!action.payload) {
        if (state.profileSummaryTokki) {
          state.profileSummaryTokki = '';
        }
        // Resetear el agente seleccionado al cerrar el rightbar
        state.agents.agentSelected = [];
      }
    },

    setModalOpen(state, action) {
      state.isModalOpen = action.payload;
    },

    setUpdateAgentView(state, action) {
      state.agentView = {
        ...state.agentView,
        ...action.payload,
      };

      // Si se activa el modo watcher (se establece selectedAgentViewChat),
      // resetear la pestaña activa a 'my_chats' para que no quede ninguna marcada
      if (action.payload.selectedAgentViewChat) {
        state.filters.activeTab = 'my_chats';
      }
    },

    setMessageReply(state, action) {
      state.messageReply = action.payload;
    },

    setMicrophonePermissionDenied(state, action) {
      state.microphonePermissionDenied = action.payload;
    },

    setSelectedAgent(state, action) {
      const { agentSelected } = state.agents;

      const hasAgent = agentSelected.find(
        (agent) => agent.id === action.payload?.id
      );

      if (hasAgent) {
        state.agents.agentSelected = agentSelected.filter(
          (agent) => agent.id !== action.payload.id
        );
      } else {
        state.agents.agentSelected = action.payload ? [action.payload] : [];
      }
    },

    setOpenModalDeleteChannel(state, action) {
      state.openModalDeleteChannel = action.payload;
    },

    setOpenModalLinkNewBranch(state, action) {
      state.openModalLinkNewBranch = action.payload;
    },

    setPermissions(state, action) {
      state.permissions = action.payload;
    },

    setChannelUpdated(state, action) {
      state.channelUpdated = action.payload;
    },

    setUserUpdated(state, action) {
      state.user = action.payload;
    },

    incrementChannelListReloadKey(state) {
      state.channelListReloadKey += 1;
      state.channelListError = null;
    },

    setChannelListError(state, action) {
      state.channelListError = action.payload;
    },

    clearChannelListError(state) {
      state.channelListError = null;
    },

    showTokkoChat(state, action) {
      const wasOpen = Boolean(state.show);
      state.show = action.payload;

      state.CID = action.payload?.CID;

      if (action.payload?.pendingChatAction) {
        state.pendingChatAction = {
          property: action.payload.pendingChatAction.property || null,
        };
      }

      if (!action.payload) {
        Object.assign(state, {
          ...initialState,
          data: state.data,
          filters: state.filters,
        });
        // Resetear los valores seleccionados en los dropdowns al cerrar el chat
        state.agentView = {
          ...state.agentView,
          selectedAgentViewChat: null,
          selectedBranchView: null,
        };
      } else if (
        // Only on closed -> open: the list reload that follows clears the flag.
        // Re-dispatching while open (e.g. open a CID) triggers no reload.
        !wasOpen &&
        Object.keys(state.channelCatalog).length === 0 &&
        Object.keys(state.channelCatalogByChatId).length === 0
      ) {
        state.channelCatalogFetchInFlight = true;
      }
    },

    setShowModalWhatsapp(state, action) {
      const { value, key } = action.payload;

      state.whatsapp[key] = value;
      state.isLoadingWhatsAppUnlinked = false;
    },

    setPlayingAudio(state, action) {
      state.playingAudio = action.payload;
    },
    stopPlayingAudio(state) {
      state.playingAudio = null;
    },

    sharePropertiesChangeValues(state, action) {
      const { key, value } = action.payload;

      // selectedProperties dispatch contract (value):
      //   []              → clears selection
      //   propertyObject  → toggles by property.id (adds or removes from the array)
      // Redux state is always Property[]. Do not pass [propertyObject]:
      // the reducer expects a single object for toggle, not a replacement array.
      if (key === 'selectedProperties') {
        if (Array.isArray(value) && value.length === 0) {
          state.shareProperties.selectedProperties = [];
          return;
        }

        const selectedProperties =
          state.shareProperties.selectedProperties || [];

        const isAlreadySelected = selectedProperties.some(
          (selected) => selected.id === value.id
        );

        state.shareProperties.selectedProperties = isAlreadySelected
          ? selectedProperties.filter((selected) => selected.id !== value.id)
          : [...selectedProperties, value];

        return;
      }

      // Limpiar estado si se cierra el modal
      if (key === 'show' && !value) {
        Object.assign(state.shareProperties, {
          ...initialState.shareProperties,
          selectedProperties: state.shareProperties.selectedProperties,
        });

        Object.assign(
          state.featuredProperties,
          initialState.featuredProperties
        );
      }

      state.shareProperties[key] = value;
    },

    clearFeaturedProperties(state) {
      state.featuredProperties.properties = [];
      state.featuredProperties.loading = false;
    },

    setTemplateReplySelectedProperty(state, action) {
      state.templateReplySelectedProperty = action.payload;
    },

    clearTemplateReplySelectedProperty(state) {
      state.templateReplySelectedProperty = null;
    },

    openModal: (state, action) => {
      state.modal.type = action.payload.type;
      state.modal.props = action.payload.props || {};
      state.modal.messageId = action.payload.messageId || null;
    },

    closeModal: (state) => {
      state.modal.type = null;
      state.modal.props = {};
      state.modal.messageId = null;
    },

    setWhatsappLinkedData(state, action) {
      state.whatsappLinkedData = action.payload;
    },

    setWhatsappLinkedUser(state, action) {
      state.whatsappLinkedUser = action.payload;
    },

    setRequestedQRType(state, action) {
      state.whatsapp.requestedQRType = action.payload;
    },

    resetWhatsappQR(state) {
      state.whatsapp.qr = false;
    },

    setCanWriteFromBranch(state, action) {
      state.whatsapp.canWriteFromBranch = action.payload;
    },

    setBranchToLinkWhatsapp(state, action) {
      state.whatsapp.branchToLink = action.payload;
    },

    setSlotAvailable(state, action) {
      state.whatsapp.availableSlot = action.payload;
    },

    removeSlotAvailable(state) {
      state.whatsapp.availableSlot = null;
    },

    setSlotsAvailability(state, action) {
      state.whatsapp.slotsAvailability = action.payload;
    },

    showWhatsAppLinkingView(state, action) {
      state.whatsapp.linkingView.isVisible = true;
      state.whatsapp.linkingView.selectedSlot = action.payload || null;
      state.whatsapp.linkingView.currentStep = 'info';
      state.whatsapp.linkingView.isConfirmed = false;
      state.whatsapp.linkingView.isScanned = false;
    },

    hideWhatsAppLinkingView(state) {
      state.whatsapp.linkingView.isVisible = false;
      // Reutilizar resetLinkingView para mantener consistencia
      MessengerSlice.caseReducers.resetLinkingView(state);
    },

    setLinkingViewStep(state, action) {
      state.whatsapp.linkingView.currentStep = action.payload;
    },

    setLinkingViewConfirmed(state, action) {
      state.whatsapp.linkingView.isConfirmed = action.payload;
    },

    setLinkingViewScanned(state) {
      state.whatsapp.linkingView.isScanned = true;
      state.whatsapp.linkingView.currentStep = 'success';
    },

    resetLinkingView(state) {
      state.whatsapp.linkingView.currentStep = 'info';
      state.whatsapp.linkingView.isConfirmed = false;
      state.whatsapp.linkingView.isScanned = false;
      state.whatsapp.linkingView.selectedSlot = null;
      state.whatsapp.linkingView.duplicatePhoneError = null;
    },

    setLinkingViewDuplicateError(state, action) {
      state.whatsapp.linkingView.duplicatePhoneError = action.payload;
      state.whatsapp.linkingView.currentStep = 'error';
    },

    showBlockedNumbersView(state) {
      state.blockedNumbersView.isVisible = true;
    },

    hideBlockedNumbersView(state) {
      state.blockedNumbersView.isVisible = false;
    },

    clearBlockedCellphone(state) {
      state.blockedCellphone = null;
    },

    setProfileSummaryTokki(state, action) {
      state.profileSummaryTokki = action.payload;
    },

    setIsFetchingProfileSummaryTokki(state, action) {
      state.isFetchingProfileSummaryTokki = action.payload;
    },

    setFileValidation(state, action) {
      state.fileValidation = {
        ...state.fileValidation,
        ...action.payload,
      };
    },

    clearFileValidation(state) {
      state.fileValidation = initialState.fileValidation;
    },

    setSearchQuery(state, action) {
      state.search.query = action.payload;
    },

    clearSearch(state) {
      state.search = initialState.search;
    },

    setPendingJumpToMessage(state, action) {
      state.pendingJumpToMessage = {
        messageId: action.payload.messageId,
        channelId: action.payload.channelId,
        searchQuery: action.payload.searchQuery || null,
      };
    },

    clearPendingJumpToMessage(state) {
      state.pendingJumpToMessage = {
        messageId: null,
        channelId: null,
        searchQuery: null,
      };
    },

    clearPendingChatProperty(state) {
      state.pendingChatAction.property = null;
    },

    setChannelCatalog(state, action) {
      const { payload } = action;
      const items = Array.isArray(payload) ? payload : payload?.items ?? [];
      const replace = Array.isArray(payload)
        ? false
        : Boolean(payload?.replace);

      if (replace) {
        MessengerSlice.caseReducers.clearChannelCatalog(state, {
          type: 'chat/clearChannelCatalog',
          payload: payload?.preserveActiveStreamCid
            ? { preserveActiveStreamCid: true }
            : {},
        });
      }

      items.forEach((item) => {
        if (!item?.streamCid) return;
        state.channelCatalog[item.streamCid] = item;
        if (item.chatChannelId != null) {
          state.channelCatalogByChatId[item.chatChannelId] = item.streamCid;
        }
      });
    },

    upsertChannelCatalogItem(state, action) {
      const item = action.payload;
      if (!item?.streamCid) return;
      state.channelCatalog[item.streamCid] = {
        ...state.channelCatalog[item.streamCid],
        ...item,
      };
      if (item.chatChannelId != null) {
        state.channelCatalogByChatId[item.chatChannelId] = item.streamCid;
      }
    },

    setActiveStreamCid(state, action) {
      state.activeStreamCid = action.payload ?? null;
    },

    clearChannelCatalog(state, action) {
      state.channelCatalog = {};
      state.channelCatalogByChatId = {};
      if (!action.payload?.preserveActiveStreamCid) {
        state.activeStreamCid = null;
      }
    },

    setChannelCatalogFetchInFlight(state, action) {
      state.channelCatalogFetchInFlight = Boolean(action.payload);
    },
  },

  extraReducers: (builder) => {
    builder.addCase(setMessengerUserDataAsync.fulfilled, (state, action) => {
      state.data = action.payload;

      MessengerSlice.caseReducers.setChannelTray(state, {
        type: 'chat/setChannelTray',
        payload: 'all',
      });
    });

    builder // get agents
      .addCase(getAgentsChat.pending, (state) => {
        state.agents.loading = true;
      })
      .addCase(getAgentsChat.fulfilled, (state, action) => {
        state.agents.loading = false;
        state.agents.list = action.payload.users;
      })
      .addCase(getAgentsChat.rejected, (state) => {
        state.agents.loading = false;
      });

    builder // get duplicate contacts
      .addCase(getDuplicateContacts.pending, (state) => {
        state.duplicateContacts.loading = true;
      })
      .addCase(getDuplicateContacts.fulfilled, (state, action) => {
        state.duplicateContacts = { ...action.payload, loading: false };
      })
      .addCase(getDuplicateContacts.rejected, (state) => {
        state.duplicateContacts.loading = false;
      });

    builder // messenger Manager
      .addCase(messengerManager.pending, (state) => {
        state.agents.asingLoader = true;
      })
      .addCase(messengerManager.fulfilled, (state) => {
        state.agents.asingLoader = false;
      })
      .addCase(messengerManager.rejected, (state) => {
        state.agents.asingLoader = false;
      });

    builder // get contacts
      .addCase(getContact.fulfilled, (state, action) => {
        const newContact = action.payload.contacts[0];

        const existingIndex = state.contacts.findIndex(
          (contact) => contact?.id === newContact?.id
        );

        if (existingIndex !== -1) {
          state.contacts[existingIndex] = newContact;
        } else {
          state.contacts.push(newContact);
        }
      });

    builder
      .addCase(getLeadsStatus.pending, (state) => {
        state.leadsStatusRequest = 'loading';
      })
      .addCase(getLeadsStatus.fulfilled, (state, action) => {
        state.leadsStatusList = Array.isArray(action.payload)
          ? action.payload
          : [];
        state.leadsStatusRequest = 'succeeded';
      })
      .addCase(getLeadsStatus.rejected, (state) => {
        state.leadsStatusRequest = 'failed';
      });

    builder // change lead status
      .addCase(changeLeadStatus.fulfilled, (state) => {
        state.loading = false;
      });

    builder // get properties to share
      .addCase(getPropertiesToShare.pending, (state) => {
        state.shareProperties.loading = true;
      })
      .addCase(getPropertiesToShare.fulfilled, (state, action) => {
        const isFirstPage =
          !action.meta.arg?.page || action.meta.arg?.page === 1;

        state.shareProperties.loading = false;
        state.shareProperties.pageInfo = action.payload.page_info;

        if (isFirstPage) {
          state.shareProperties.properties = action.payload.properties;
        } else {
          const { properties } = state.shareProperties;
          const newProperties = action.payload.properties.filter(
            (newProperty) =>
              !properties.some(
                (existingProperty) => existingProperty.id === newProperty.id
              )
          );
          state.shareProperties.properties = [...properties, ...newProperties];
        }
      });

    builder // get developments to share
      .addCase(getDevelopmentsToShare.pending, (state) => {
        state.shareProperties.loading = true;
      })
      .addCase(getDevelopmentsToShare.fulfilled, (state, action) => {
        const isFirstPage =
          !action.meta.arg?.page || action.meta.arg?.page === 1;

        state.shareProperties.loading = false;
        state.shareProperties.pageInfo = action.payload.page_info;

        if (isFirstPage) {
          state.shareProperties.properties = action.payload.results;
        } else {
          const { properties } = state.shareProperties;
          const newDevelopments = action.payload.results.filter(
            (newDevelopment) =>
              !properties.some(
                (existingDevelopment) =>
                  existingDevelopment.id === newDevelopment.id
              )
          );
          state.shareProperties.properties = [
            ...properties,
            ...newDevelopments,
          ];
        }
      });

    builder // getStarredsForEntity
      .addCase(getStarredsForEntity.pending, (state) => {
        state.featuredProperties.loading = true;
      })
      .addCase(getStarredsForEntity.fulfilled, (state, action) => {
        state.featuredProperties.properties = action.payload;

        state.featuredProperties.loading = false;
      });

    builder // get quick responses
      .addCase(getQuickResponses.pending, (state) => {
        state.quickResponses.loading = true;
      })
      .addCase(getQuickResponses.fulfilled, (state, action) => {
        state.quickResponses.loading = false;
        state.quickResponses.list = action.payload;
      });

    builder.addCase(deleteWebcontact.fulfilled, () => {
      // solo llamamos al endpoint
    });

    // post create channel
    builder.addCase(postCreateChannel.fulfilled, () => {
      // solo llamamos al endpoint
    });

    // delete message
    builder.addCase(deleteMessage.fulfilled, () => {
      // solo llamamos al endpoint
    });

    builder // faild message and status swatch
      .addCase(sendWhatsappMessage.pending, (state) => {
        state.reSendMessageWhatsapp.loading = true;
      })
      .addCase(sendWhatsappMessage.fulfilled, (state) => {
        state.reSendMessageWhatsapp.loading = false;
      })
      .addCase(sendWhatsappMessage.rejected, (state) => {
        state.reSendMessageWhatsapp.loading = false;
      });

    // Añadir el caso para updateChannelData
    builder.addCase(updateChannelData.fulfilled, (state, action) => {
      state.channelData = action.payload;
      state.channelUpdated = false; // Asegurarse de que channelUpdated se restablezca
    });

    // blockChannel
    builder
      .addCase(blockChannel.pending, (state) => {
        state.blockLoading = true;
      })
      .addCase(blockChannel.fulfilled, (state, action) => {
        state.blockLoading = false;
        state.blockedCellphone = action.payload?.cellphone ?? null;
      })
      .addCase(blockChannel.rejected, (state) => {
        state.blockLoading = false;
      });

    // unblockChannel
    builder
      .addCase(unblockChannel.pending, (state) => {
        state.unblockLoading = true;
      })
      .addCase(unblockChannel.fulfilled, (state) => {
        state.unblockLoading = false;
        state.blockedCellphone = null;
      })
      .addCase(unblockChannel.rejected, (state) => {
        state.unblockLoading = false;
      });

    // acceptChannel
    builder
      .addCase(acceptChannel.pending, (state) => {
        state.acceptLoading = true;
      })
      .addCase(acceptChannel.fulfilled, (state) => {
        state.acceptLoading = false;
      })
      .addCase(acceptChannel.rejected, (state) => {
        state.acceptLoading = false;
      });

    // searchChat
    builder
      .addCase(searchChat.pending, (state, action) => {
        state.search.loading = true;
        const newQuery = action.meta?.arg?.q || '';
        const isFirstPage =
          !action.meta?.arg?.page || action.meta.arg.page === 1;
        const queryChanged = state.search.query !== newQuery;

        // Actualizar el query cuando se inicia la búsqueda
        if (action.meta?.arg?.q !== undefined) {
          state.search.query = newQuery;
        }

        // Si es primera página y el query cambió, limpiar resultados anteriores
        // para evitar mostrar resultados de la búsqueda anterior
        if (isFirstPage && queryChanged) {
          state.search.channels = [];
          state.search.contacts = [];
          state.search.messages = [];
        }
      })
      .addCase(searchChat.fulfilled, (state, action) => {
        const { channels, contacts, messages, pagination } = action.payload;
        const isFirstPage = pagination.page === 1;

        state.search.loading = false;

        if (isFirstPage) {
          state.search.channels = channels;
          state.search.contacts = contacts;
          state.search.messages = messages;
        } else {
          state.search.channels = [...state.search.channels, ...channels];
          state.search.contacts = [...state.search.contacts, ...contacts];
          state.search.messages = [...state.search.messages, ...messages];
        }

        state.search.pagination = pagination;
      })
      .addCase(searchChat.rejected, (state) => {
        state.search.loading = false;
      });

    builder
      .addCase(fetchBlockedChannels.pending, (state) => {
        state.blockedNumbersView.loading = true;
      })
      .addCase(fetchBlockedChannels.fulfilled, (state, action) => {
        const payload = action.payload || {};
        const numbers = Array.isArray(payload.numbers) ? payload.numbers : [];
        const page = payload.page ?? 1;
        const totalPages = payload.total_pages ?? 1;

        state.blockedNumbersView.loading = false;
        state.blockedNumbersView.channels = numbers;
        state.blockedNumbersView.pagination = {
          page,
          limit: payload.limit ?? numbers.length,
          total: payload.count ?? numbers.length,
          total_pages: totalPages,
          has_next: page < totalPages,
          has_previous: page > 1,
        };
      })
      .addCase(fetchBlockedChannels.rejected, (state) => {
        state.blockedNumbersView.loading = false;
      });
  },
});

export const {
  setChannelData,
  showTokkoChat,
  setShowQRModal,
  setShowWhatsappAd,
  setShowModalWhatsapp,
  setSelectedAgent,
  showRightbar,
  setMessageReply,
  resetDuplicated,
  setFilter,
  setActiveTab,
  setTabHasUnread,
  setPermissions,
  sharePropertiesChangeValues,
  setTemplateReplySelectedProperty,
  clearTemplateReplySelectedProperty,
  setChannelUpdated,
  setUserUpdated,
  incrementChannelListReloadKey,
  setChannelListError,
  clearChannelListError,
  setChannelTray,
  setPlayingAudio,
  stopPlayingAudio,
  setMicrophonePermissionDenied,
  setUnreads,
  setModalOpen,
  setAttachments,
  setCountAttachments,
  setUpdateAgentView,
  setAutoAsign,
  setOpenModalDeleteChannel,
  openModal,
  closeModal,
  setWhatsappLinkedData,
  setWhatsappLinkedUser,
  clearFeaturedProperties,
  setOpenModalLinkNewBranch,
  setRequestedQRType,
  resetWhatsappQR,
  setCanWriteFromBranch,
  setBranchToLinkWhatsapp,
  setSlotAvailable,
  removeSlotAvailable,
  setSlotsAvailability,
  showWhatsAppLinkingView,
  hideWhatsAppLinkingView,
  setLinkingViewStep,
  setLinkingViewConfirmed,
  setLinkingViewScanned,
  resetLinkingView,
  setLinkingViewDuplicateError,
  setProfileSummaryTokki,
  setIsFetchingProfileSummaryTokki,
  setFileValidation,
  clearFileValidation,
  setSearchQuery,
  clearSearch,
  setPendingJumpToMessage,
  clearPendingJumpToMessage,
  showBlockedNumbersView,
  hideBlockedNumbersView,
  clearBlockedCellphone,
  clearPendingChatProperty,
  setChannelCatalog,
  upsertChannelCatalogItem,
  setActiveStreamCid,
  clearChannelCatalog,
  setChannelCatalogFetchInFlight,
} = MessengerSlice.actions;

export default MessengerSlice.reducer;
